// apps/app/src/features/auth/register.page.tsx
//
// No card at signup. [ADR 0008] Licence verification is a stronger filter than a
// card, and cost is per scan rather than per signup — so a dormant account costs
// nothing and the card is asked for at go-live instead.

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { Building2, LockKeyhole, Mail, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/lib/auth';
import { ApiError } from '@/lib/api-client';
import { intlLocale } from '@/lib/i18n';
import { AuthField, AuthHeading, AuthLayout, IconInput, PasswordInput } from './auth-layout';

// Where clinics sign up from. The API takes any ISO code; this keeps the list short.
const COUNTRIES = [
  'AT', 'AU', 'BD', 'BE', 'CA', 'CH', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GB', 'GR', 'HR', 'HU',
  'IE', 'IN', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'NO', 'NZ', 'PL', 'PT', 'RO', 'SE', 'SG', 'SI', 'SK', 'AE', 'US',
];
const EURO = new Set(['AT', 'BE', 'CY', 'DE', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PT', 'SI', 'SK']);
// The currencies plans are priced in.
const CURRENCIES = ['EUR', 'GBP', 'USD'];

function currencyFor(country: string): string {
  return EURO.has(country) ? 'EUR' : country === 'GB' ? 'GBP' : 'USD';
}

/** The browser's region, when it is one we list; otherwise GB. */
function guessCountry(): string {
  const region = navigator.language.split('-')[1]?.toUpperCase();
  return region && COUNTRIES.includes(region) ? region : 'GB';
}

export function RegisterPage() {
  const { t } = useTranslation('auth');
  const register = useAuth((s) => s.register);
  const navigate = useNavigate();

  const [form, setForm] = useState(() => {
    const country = guessCountry();
    return { clinicName: '', email: '', password: '', country, currency: currencyFor(country) };
  });
  // Currency follows the country until somebody picks one themselves.
  const [currencyTouched, setCurrencyTouched] = useState(false);
  const countries = useMemo(() => {
    const names = new Intl.DisplayNames([intlLocale()], { type: 'region' });
    return COUNTRIES.map((code) => ({ code, name: names.of(code) ?? code })).sort((a, b) => a.name.localeCompare(b.name));
  }, []);
  const [acceptedDpa, setAcceptedDpa] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setErrors({});

    try {
      await register({
        ...form,
        acceptedDpa: true,
        // Detected rather than asked. One fewer question, and almost always right.
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      });
      navigate('/onboarding', { replace: true });
    } catch (err) {
      setErrors(
        err instanceof ApiError
          ? Object.keys(err.fieldErrors).length > 0
            ? err.fieldErrors
            : { _: err.message }
          : { _: t('register.error') },
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout aside={{ text: t('register.haveAccountText'), action: t('register.signIn'), to: '/login' }}>
      <AuthHeading icon={UserPlus} title={t('register.title')} subtitle={t('register.subtitle')} />

      <form onSubmit={submit} className="space-y-5">
        <AuthField id="reg-clinic" label={t('fields.clinicName')} error={errors['clinicName']}>
          <IconInput
            id="reg-clinic"
            icon={Building2}
            required
            autoComplete="organization"
            placeholder={t('placeholders.clinicName')}
            value={form.clinicName}
            onChange={(e) => setForm({ ...form, clinicName: e.target.value })}
          />
        </AuthField>

        <AuthField id="reg-email" label={t('fields.email')} error={errors['email']}>
          <IconInput
            id="reg-email"
            icon={Mail}
            type="email"
            required
            autoComplete="email"
            placeholder={t('placeholders.email')}
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </AuthField>

        {/* Length beats composition rules — a 12-character passphrase is
            stronger than P@ssw0rd! and people stop writing them down. */}
        <AuthField id="reg-password" label={t('fields.password')} error={errors['password']} hint={t('password.minLengthHint')}>
          <PasswordInput
            id="reg-password"
            icon={LockKeyhole}
            required
            minLength={12}
            autoComplete="new-password"
            placeholder={t('placeholders.newPassword')}
            aria-describedby={errors['password'] ? 'reg-password-error' : 'reg-password-hint'}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
        </AuthField>

        <div className="grid grid-cols-[minmax(0,1fr)_120px] gap-3">
          <AuthField id="reg-country" label={t('fields.country')} error={errors['country']}>
            <Select
              value={form.country}
              onValueChange={(country) =>
                setForm({ ...form, country, currency: currencyTouched ? form.currency : currencyFor(country) })
              }
            >
              <SelectTrigger id="reg-country" className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {countries.map((c) => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </AuthField>
          <AuthField id="reg-currency" label={t('fields.currency')} error={errors['currency']}>
            <Select
              value={form.currency}
              onValueChange={(currency) => {
                setCurrencyTouched(true);
                setForm({ ...form, currency });
              }}
            >
              <SelectTrigger id="reg-currency" className="h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </AuthField>
        </div>

        {/* Clinics ask for this, and having it ready closes deals. */}
        <div className="flex items-start gap-2.5 text-sm">
          <Checkbox
            id="reg-dpa"
            required
            checked={acceptedDpa}
            onCheckedChange={(v) => setAcceptedDpa(v === true)}
            className="mt-0.5"
          />
          <Label htmlFor="reg-dpa" className="font-normal leading-snug text-muted-foreground">
            <Trans
              t={t}
              i18nKey="register.dpa"
              components={{
                anchor: <a href="https://skinvizo.com/dpa" target="_blank" rel="noreferrer" className="text-foreground underline underline-offset-2" />,
              }}
            />
          </Label>
        </div>

        {errors['_'] && (
          <p className="text-sm text-destructive" role="alert">
            {errors['_']}
          </p>
        )}

        <Button loading={busy} disabled={!acceptedDpa} className="h-11 w-full">
          {t('register.submit')}
        </Button>
      </form>
    </AuthLayout>
  );
}
